import { Skeleton, Stack, Typography } from '@mui/material'
import { FeatureFlagIdReviewsPendingActions } from 'c1gen/features'
import { C1Chip } from 'c1ui/components/C1Chip'
import { skeletonFadeInSx } from 'c1ui/theme/C1Transitions'
import { C1Card } from 'components/core/card/C1Card'
import { EmptyListWithIllustration } from 'components/core/empty-list/EmptyListWithIllustration'
import { C1PagedListHook } from 'components/core/list/C1List/C1PagedListHook'
import { DueDateChip } from 'components/core/time/display'
import { useFeatureChecker } from 'components/no-ui/capability'
import { ReviewStylePickerModal } from 'components/pages/my_tasks/access_reviews/common/review-style-picker'
import { getReviewStyleFromStorageOrDefault } from 'components/pages/my_tasks/access_reviews/common/review-style-picker/review-style-local-storage'
import { UserContext } from 'lib/context/app'
import { useSimpleModal } from 'lib/modals'
import { Route, routes } from 'lib/routes'
import { AccessReviewViewType } from 'pbts/c1/api/accessreview/v1/accessreview'
import { GetPendingSummaryRequest } from 'pbts/c1/api/accessreview/v1/accessreview_pending'
import { AccessReviewSearchServiceRollupRequest } from 'pbts/c1/api/accessreview/v1/accessreview_search'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { accessReviewRollupApi, AccessReviewRollupValue } from 'redux_toolkit/access_review/access_review_rollup'
import { getPendingSummaryApi } from 'redux_toolkit/access_review/pending_actions'

const pageSize = 10

export const ReviewCampaignsOverviewList: FC = () => {
    const userContext = useContext(UserContext)
    const userId = userContext.user.id
    const intl = useIntl()

    const table = useMemo(
        () => (
            <C1PagedListHook<AccessReviewSearchServiceRollupRequest, AccessReviewRollupValue>
                hook={accessReviewRollupApi.useRollup}
                request={AccessReviewSearchServiceRollupRequest.create({ userId })}
                hookFlags={{ pageSize, actionable: !!userId }}
                emptyState={
                    <EmptyListWithIllustration
                        title={intl.formatMessage({
                            id: 'review_empty_title',
                            defaultMessage: "You're all good!",
                        })}
                        body={intl.formatMessage({
                            id: 'review_empty_body',
                            defaultMessage: 'You currently are not assigned any access reviews. We’ll notify you if you’re assigned any reviews in the future. For now, go relax.',
                        })}
                        desktopImageProps={{
                            width: 1000,
                            height: 165,
                            src: '/static/review_empty.png',
                        }}
                        mobileImageProps={{
                            width: 120,
                            height: 120,
                            src: '/static/review_empty_mobile.svg',
                        }}
                        buttonText={intl.formatMessage({
                            id: 'review_empty_button_go_home',
                            defaultMessage: 'Go home',
                        })}
                        buttonHref={routes.home}
                    />
                }
                hidePageLabel
                ListItemComponent={AccessReviewListItem}
                ListItemSkeleton={AccessReviewListItemSkeleton}
                sx={{ overflow: 'visible' }}
            />
        ),
        [intl, userId],
    )

    return table
}

type AccessReviewListItemProps = {
    row: AccessReviewRollupValue
}

export const AccessReviewListItem: FC<AccessReviewListItemProps> = ({ row }) => {
    const { open } = useSimpleModal()

    const intl = useIntl()

    const reviewStyle = getReviewStyleFromStorageOrDefault(row.accessReview?.id, row.accessReview?.defaultView)

    const assignedTasks = Math.max(0, row.assignedTasks ?? 0)

    const hasPendingActionsFF = useFeatureChecker(FeatureFlagIdReviewsPendingActions) ?? false
    const { data: pendingSummaryResponse } = getPendingSummaryApi.useGetPendingSummary(
        GetPendingSummaryRequest.create({ accessReviewId: row.accessReview?.id }),
        { notify: true, actionable: !!(hasPendingActionsFF && row.accessReview?.id) },
    )

    const pendingCount = pendingSummaryResponse?.summary?.totalCount ?? 0
    const tasksWithoutPending = hasPendingActionsFF ? Math.max(0, assignedTasks - pendingCount) : assignedTasks

    const itemInner = <Stack spacing={3} display="flex" flex={1}>
        <Stack direction="row" flexWrap="wrap" sx={{ columnGap: 1, rowGap: 1 }}>
            {tasksWithoutPending > 0 && <C1Chip
                size="small"
                color="primary"
                label={intl.formatMessage({
                    id: 'access_review_rollup_tasks_remaining',
                    defaultMessage: '{remaining} {remaining, plural, one {task} other {tasks}} remaining',
                }, { remaining: tasksWithoutPending })}
            />}
            {hasPendingActionsFF && tasksWithoutPending === 0 && assignedTasks > 0 && <C1Chip
                size="small"
                color="warning"
                label={intl.formatMessage({
                    id: 'access_review_needs_submission',
                    defaultMessage: 'Needs submission',
                })}
            />}
            {assignedTasks === 0 && <C1Chip
                color="success"
                size="small"
                label={intl.formatMessage({
                    id: 'access_review_rollup_tasks_completed',
                    defaultMessage: 'Completed',
                })}
            />}
            {row.accessReview?.completionDate && assignedTasks > 0 && <DueDateChip
                size="small"
                dueDate={Timestamp.toDate(row.accessReview?.completionDate)}
            />}
        </Stack>

        <Stack spacing={2}>
            <Typography variant="h5" component="div">{row.accessReview?.displayName}</Typography>
            {row.accessReview?.description && <Typography
                color="text.primary"
                sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {row.accessReview?.description}
            </Typography>}
        </Stack>
    </Stack>

    const getHrefForReviewStyle = (style: AccessReviewViewType): Route | undefined => {
        if (!row.accessReview?.id) return undefined
        const accessReviewId = row.accessReview.id
        switch (style) {
            case AccessReviewViewType.BY_USER:
                return routes.assignments.reviews.byUser(accessReviewId)
            case AccessReviewViewType.BY_APP:
                return routes.assignments.reviews.byApp(accessReviewId)
            case AccessReviewViewType.UNSTRUCTURED:
                return routes.assignments.reviews.unstructured(accessReviewId)
            default:
                return undefined
        }
    }

    return <C1Card
        sx={{ borderColor: 'border.subtle', opacity: assignedTasks === 0 ? 0.75 : 1 }}
        href={reviewStyle ? getHrefForReviewStyle(reviewStyle) : undefined}
        onClick={reviewStyle
            ? undefined
            : (): void => {
                if (!row.accessReview?.id) return

                open(ReviewStylePickerModal, {
                    accessReviewID: row.accessReview.id,
                })()
            }}
    >
        {itemInner}
    </C1Card>
}

export const AccessReviewListItemSkeleton: FC = () => {
    const jitterWidths = useMemo(() => ({
        title: 500 * (0.8 + Math.random() * 0.4),
        subtitle: 450 * (0.8 + Math.random() * 0.4),
        button: 96 * (0.8 + Math.random() * 0.4),
    }), [])

    return <C1Card sx={skeletonFadeInSx}>
        <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
            <Stack spacing={1}>
                <Skeleton variant="rectangular" height={24} width={jitterWidths.title} />
                <Skeleton variant="rectangular" height={12} width={jitterWidths.subtitle} />
            </Stack>
            <Stack direction="row" spacing={1}>
                <Skeleton variant="rectangular" height={30} width={jitterWidths.button} />
                <Skeleton variant="rectangular" height={30} width={jitterWidths.button} />
            </Stack>
        </Stack>
    </C1Card>
}
